import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Shield, TrendingUp, Users, CheckCircle2, ArrowRight,
  Play, FileText, ChevronRight, Star, Zap, Globe, Target, 
  BarChart3, Layers, Home, CreditCard, Calculator,
  Smartphone, Bot, ExternalLink, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const EcosystemInvestorPage = () => {
  const navigate = useNavigate();
  const [expandedCompany, setExpandedCompany] = useState(null);

  return (
    <div className="min-h-screen bg-background" data-testid="ecosystem-investor">
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/10 bg-background/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-lg text-primary-foreground font-bold">S</span>
              </div>
              <div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Subarashi</span>
                <span className="text-muted-foreground text-sm ml-2">Ecosystem</span>
              </div>
              <Badge variant="gold" className="ml-2">INVESTOR DECK</Badge>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/ecosystem/deck')} className="border-gold-600/30">
                <FileText className="w-4 h-4 mr-2" /> PDF Deck
              </Button>
              <Button className="btn-gold-metallic">
                <a href="mailto:admin@subarashirealestate.com">Contact Us</a>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="gold" className="mb-6">THE SUBARASHI ECOSYSTEM</Badge>
            <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-6">
              Building the Future of<br />
              <span className="text-primary">Real Estate in UAE</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              A vertically integrated ecosystem combining traditional brokerage excellence 
              with cutting-edge technology platforms. Three companies, one vision.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="btn-gold-metallic">
                View Full Presentation <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" size="lg" className="border-white/20">
                <a href="https://www.subarashirealestate.com" target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <Globe className="w-5 h-5 mr-2" /> Visit Subarashi
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Three Pillars */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge variant="gold" className="mb-4">THE ECOSYSTEM</Badge>
            <h2 className="font-display text-4xl mb-4">Three Pillars, One Vision</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each company addresses a critical part of the real estate value chain
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Subarashi Real Estate */}
            <Card className="glass-card border-white/10 hover:border-primary/30 transition-all group">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-lg bg-[#043D32] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="w-8 h-8 text-[#C6A962]" />
                </div>
                <Badge className="bg-green-600/20 text-green-400 mb-4">ACTIVE</Badge>
                <h3 className="font-display text-2xl mb-3">Subarashi Real Estate</h3>
                <p className="text-muted-foreground mb-6">
                  Premium brokerage services for luxury property transactions in Dubai. 
                  RERA licensed, DLD registered, serving discerning clients.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Luxury Listings & Sales</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Holiday Homes Management</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Area Guides & Market Intelligence</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Subarashi Flex (Rent Financing)</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full border-white/20" asChild>
                  <a href="https://www.subarashirealestate.com" target="_blank" rel="noopener noreferrer">
                    Visit Website <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Rashii */}
            <Card className="glass-card border-primary/30 ring-2 ring-primary/30 hover:ring-primary/50 transition-all group">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-lg logo-icon-metallic flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="font-display text-2xl text-primary-foreground font-bold">R</span>
                </div>
                <Badge variant="gold" className="mb-4">BUILDING</Badge>
                <h3 className="font-display text-2xl mb-3">Rashii Platform</h3>
                <p className="text-muted-foreground mb-6">
                  Decision intelligence for verified real estate transactions. 
                  The technology layer powering trust and transparency.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>KYC Verified Exchange</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Rashii Score (AI Pricing)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Structured Negotiation</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Escrow Settlement</span>
                  </div>
                </div>
                <Button className="w-full btn-gold-metallic" onClick={() => navigate('/investor')}>
                  View Rashii Deck <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Suba */}
            <Card className="glass-card border-white/10 hover:border-blue-500/30 transition-all group">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <Badge className="bg-blue-600/20 text-blue-400 mb-4">LIVE</Badge>
                <h3 className="font-display text-2xl mb-3">Subaa</h3>
                <p className="text-muted-foreground mb-6">
                  Flexible rent financing for UAE tenants. Split annual rent 
                  into 12 easy monthly payments at just 6% flat fee.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    <span>12-Month Payment Plans</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    <span>6% Transparent Flat Fee</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    <span>AI Chatbot & Mobile Apps</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    <span>172K+ Properties Database</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10" asChild>
                  <a href="https://subaa.ae" target="_blank" rel="noopener noreferrer">
                    Visit Subaa <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ecosystem Synergy */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">SYNERGY</Badge>
            <h2 className="font-display text-4xl mb-4">The Complete Real Estate Journey</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each company feeds into the others, creating a virtuous cycle of value
            </p>
          </div>

          <div className="relative">
            {/* Connection Lines (simplified visual) */}
            <div className="hidden lg:block absolute inset-0 z-0">
              <svg className="w-full h-full" viewBox="0 0 1000 400">
                <path d="M200 200 L500 100 L800 200" stroke="rgba(198,169,98,0.3)" strokeWidth="2" fill="none" />
                <path d="M200 200 L500 300 L800 200" stroke="rgba(198,169,98,0.3)" strokeWidth="2" fill="none" />
                <path d="M500 100 L500 300" stroke="rgba(198,169,98,0.3)" strokeWidth="2" fill="none" />
              </svg>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 relative z-10">
              {/* Flow 1 */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-[#043D32] flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-10 h-10 text-[#C6A962]" />
                </div>
                <h3 className="font-display text-xl mb-2">Find Property</h3>
                <p className="text-sm text-muted-foreground">
                  Subarashi agents help clients discover premium properties with expert guidance
                </p>
              </div>

              {/* Flow 2 */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full logo-icon-metallic flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-2xl text-primary-foreground font-bold">R</span>
                </div>
                <h3 className="font-display text-xl mb-2">Transact Safely</h3>
                <p className="text-sm text-muted-foreground">
                  Rashii ensures verified, transparent, and structured transactions
                </p>
              </div>

              {/* Flow 3 */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-display text-xl mb-2">Finance Flexibly</h3>
                <p className="text-sm text-muted-foreground">
                  Subaa enables tenants to afford premium rentals with 12-month payments
                </p>
              </div>
            </div>
          </div>

          {/* Cross-Sell Opportunities */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-5 h-5 text-[#C6A962]" />
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <span className="font-display">R</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Subarashi listings feed into Rashii's verified marketplace, expanding reach
                </p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-display text-primary">R</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Rashii rental transactions can offer Subaa financing at checkout
                </p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <Building2 className="w-5 h-5 text-[#C6A962]" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Subaa users become leads for Subarashi sales when ready to buy
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">MARKET OPPORTUNITY</Badge>
            <h2 className="font-display text-4xl mb-4">The UAE Real Estate Market</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <Card className="glass-card border-white/10 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-display text-primary mb-2">AED 917B</div>
                <p className="text-sm text-muted-foreground">Dubai Transactions (2025)</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Source: DLD*</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-display text-primary mb-2">AED 126B</div>
                <p className="text-sm text-muted-foreground">Annual Rental Market</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Source: DLD*</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-display text-primary mb-2">1.38M</div>
                <p className="text-sm text-muted-foreground">Rental Contracts</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Source: DLD*</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-display text-primary mb-2">88%</div>
                <p className="text-sm text-muted-foreground">Expat Population</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mb-8 text-xs text-muted-foreground/50">
            *Dubai Land Department Official Statistics 2025
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#043D32] rounded-lg p-6">
              <h4 className="text-[#C6A962] font-medium mb-3">Subarashi TAM</h4>
              <div className="text-3xl font-display text-white mb-2">AED 682B</div>
              <p className="text-white/70 text-sm">Dubai property sales transactions (2025)</p>
              <p className="text-white/50 text-xs mt-1">Source: DLD</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-6 border border-primary/30">
              <h4 className="text-primary font-medium mb-3">Rashii TAM</h4>
              <div className="text-3xl font-display text-foreground mb-2">$1.55B</div>
              <p className="text-muted-foreground text-sm">UAE PropTech market by 2030</p>
              <p className="text-muted-foreground/60 text-xs mt-1">Source: TechSci Research</p>
            </div>
            <div className="bg-blue-600/10 rounded-lg p-6 border border-blue-500/30">
              <h4 className="text-blue-400 font-medium mb-3">Subaa TAM</h4>
              <div className="text-3xl font-display text-foreground mb-2">AED 12.6B</div>
              <p className="text-muted-foreground text-sm">10% of tenants seeking payment flexibility</p>
              <p className="text-muted-foreground/60 text-xs mt-1">Based on AED 126B rental market</p>
            </div>
          </div>
        </div>
      </section>

      {/* Combined Business Model */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">REVENUE MODEL</Badge>
            <h2 className="font-display text-4xl mb-4">Diversified Revenue Streams</h2>
          </div>

          <Tabs defaultValue="subarashi" className="w-full">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-8">
              <TabsTrigger value="subarashi">Subarashi</TabsTrigger>
              <TabsTrigger value="rashii">Rashii</TabsTrigger>
              <TabsTrigger value="subaa">Subaa</TabsTrigger>
            </TabsList>

            <TabsContent value="subarashi">
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">2-5%</div>
                    <h4 className="font-medium mb-1">Sales Commission</h4>
                    <p className="text-xs text-muted-foreground">2% (Secondary) | 5% (Off-Plan)</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">5%</div>
                    <h4 className="font-medium mb-1">Rental Commission</h4>
                    <p className="text-xs text-muted-foreground">Annual rent value</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">15%</div>
                    <h4 className="font-medium mb-1">Holiday Homes</h4>
                    <p className="text-xs text-muted-foreground">Management fee</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="rashii">
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">2-5%</div>
                    <h4 className="font-medium mb-1">Transaction Fee</h4>
                    <p className="text-xs text-muted-foreground">2% (Secondary) | 5% (Off-Plan)</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">AED 500</div>
                    <h4 className="font-medium mb-1">Listing Fee</h4>
                    <p className="text-xs text-muted-foreground">Per property</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">Premium</div>
                    <h4 className="font-medium mb-1">Intelligence</h4>
                    <p className="text-xs text-muted-foreground">Subscription</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-primary mb-2">Agent</div>
                    <h4 className="font-medium mb-1">Portal</h4>
                    <p className="text-xs text-muted-foreground">Subscription</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="subaa">
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-blue-400 mb-2">6%</div>
                    <h4 className="font-medium mb-1">Financing Fee</h4>
                    <p className="text-xs text-muted-foreground">Flat rate on rent</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-blue-400 mb-2">AED 500</div>
                    <h4 className="font-medium mb-1">Processing Fee</h4>
                    <p className="text-xs text-muted-foreground">Per application</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-blue-400 mb-2">1%</div>
                    <h4 className="font-medium mb-1">Agent Referral</h4>
                    <p className="text-xs text-muted-foreground">Commission share</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl font-display text-blue-400 mb-2">Add-ons</div>
                    <h4 className="font-medium mb-1">Premium Services</h4>
                    <p className="text-xs text-muted-foreground">Express, insurance</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Traction */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">TRACTION</Badge>
            <h2 className="font-display text-4xl mb-4">Progress Across the Ecosystem</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Subarashi Traction */}
            <Card className="glass-card border-[#C6A962]/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#C6A962]" />
                  Subarashi Real Estate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    RERA Licensed & DLD Registered
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Live website with listings
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Area guides for 20+ communities
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Holiday homes management
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Subarashi Flex integration
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Rashii Traction */}
            <Card className="glass-card border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="font-display text-primary">R</span>
                  Rashii Platform
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Full buyer/seller journeys built
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    KYC verification system
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Rashii Score algorithm
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Offer/counter-offer system
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Agent portal foundation
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Subaa Traction */}
            <Card className="glass-card border-blue-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Subaa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    172K+ properties in database
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Live platform at subaa.ae
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Mobile apps ready (iOS & Android)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    AI chatbot & WhatsApp integration
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Agent referral system
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Ask */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="glass-card border-primary/30 gold-glow">
            <CardContent className="p-12 text-center">
              <Badge variant="gold" className="mb-6">SEED INVESTMENT OPPORTUNITY</Badge>
              <h2 className="font-display text-4xl mb-4">Ecosystem Seed Round</h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Invest in the complete Subarashi ecosystem - three interconnected companies 
                capturing value across the entire real estate journey.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
                <div>
                  <div className="text-4xl font-display text-primary mb-2">$5M</div>
                  <p className="text-sm text-muted-foreground">Target Raise</p>
                  <p className="text-xs text-muted-foreground/70">(AED 18.4M)</p>
                </div>
                <div>
                  <div className="text-4xl font-display text-primary mb-2">$3M</div>
                  <p className="text-sm text-muted-foreground">Minimum Entry</p>
                  <p className="text-xs text-muted-foreground/70">(AED 11M)</p>
                </div>
                <div>
                  <div className="text-4xl font-display text-primary mb-2">36 mo</div>
                  <p className="text-sm text-muted-foreground">Runway</p>
                </div>
                <div>
                  <div className="text-4xl font-display text-primary mb-2">Equity</div>
                  <p className="text-sm text-muted-foreground">Investment Type</p>
                </div>
              </div>

              <h4 className="font-medium mb-6 text-lg">Use of Funds</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm mb-8">
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">30%</div>
                  <div className="text-muted-foreground text-xs">Marketing & Brand</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">25%</div>
                  <div className="text-muted-foreground text-xs">Technology Dev</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">15%</div>
                  <div className="text-muted-foreground text-xs">Operations</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">12%</div>
                  <div className="text-muted-foreground text-xs">Banking Setup</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">10%</div>
                  <div className="text-muted-foreground text-xs">RE Financing</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">8%</div>
                  <div className="text-muted-foreground text-xs">Compliance</div>
                </div>
              </div>

              <div className="text-left max-w-2xl mx-auto bg-secondary/20 rounded-lg p-6 mb-8">
                <h5 className="font-medium mb-3 text-primary">Fund Allocation Details</h5>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex gap-2"><span className="text-primary">•</span> Marketing & Brand Awareness campaigns across UAE</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Technology infrastructure, AI/ML development & platform scaling</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Running costs, team expansion & operational upkeep</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Banking facilities onboarding & payment integrations</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Real estate operations financing & holiday homes (Subaa) launch</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Regulatory compliance & licensing</li>
                </ul>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="btn-gold-metallic" asChild>
                  <a href="mailto:admin@subarashirealestate.com">
                    Schedule Meeting <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-white/20" onClick={() => navigate('/ecosystem/deck')}>
                  Download Full Deck <FileText className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl mb-8">Connect With Us</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <Building2 className="w-8 h-8 text-[#C6A962] mx-auto mb-3" />
                <h4 className="font-medium mb-2">Subarashi Real Estate</h4>
                <p className="text-sm text-muted-foreground mb-3">admin@subarashirealestate.com</p>
                <Button variant="outline" size="sm" className="border-white/20" asChild>
                  <a href="https://www.subarashirealestate.com" target="_blank" rel="noopener noreferrer">
                    Visit Site
                  </a>
                </Button>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <div className="w-8 h-8 rounded logo-icon-metallic flex items-center justify-center mx-auto mb-3">
                  <span className="font-display text-sm text-primary-foreground font-bold">R</span>
                </div>
                <h4 className="font-medium mb-2">Rashii</h4>
                <p className="text-sm text-muted-foreground mb-3">invest@rashii.ae</p>
                <Button variant="outline" size="sm" className="border-white/20" onClick={() => navigate('/investor')}>
                  View Deck
                </Button>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <CreditCard className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <h4 className="font-medium mb-2">Subaa</h4>
                <p className="text-sm text-muted-foreground mb-3">admin@subaa.ae</p>
                <Button variant="outline" size="sm" className="border-white/20" asChild>
                  <a href="https://subaa.ae" target="_blank" rel="noopener noreferrer">
                    Visit Site
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-muted-foreground">
            Office 410, AB Center, Al Barsha 1, Dubai, UAE
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-sm text-primary-foreground font-bold">S</span>
              </div>
              <span className="font-display text-lg gold-interactive">Subarashi Ecosystem</span>
            </div>
            <p className="text-xs text-muted-foreground">
              © 2025 Subarashi. Confidential Investor Materials.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EcosystemInvestorPage;
